import styled from "styled-components";
import { motion } from "framer-motion";
import { NavLink } from "react-router-dom";
import { BP_TABLET, BP_MOBILE } from "../../styles/canonical/breakpoints";

// TOPBAR_HEIGHT legacy — canonical é var(--topbar-height) = 56px.
export const TOPBAR_HEIGHT = 56;
// Drawer/mobile threshold — Bloco D: abaixo de 1024px a sidebar vira drawer
// e o burger aparece. (Antes era 920px.)
export const MOBILE_BREAKPOINT = BP_TABLET;

export const HeaderWrapper = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  /* PWA iOS (status bar black-translucent): o conteudo vai por baixo da
     Dynamic Island/notch. Reservamos --safe-area-top no topo (via padding e
     altura extra) pro header nao ficar escondido. Fora do PWA, --safe-area-top
     = 0, entao nada muda. box-sizing garante os 56px de conteudo abaixo da
     inset independente do reset. */
  box-sizing: border-box;
  padding: var(--safe-area-top, 0px) var(--space-5) 0;
  height: calc(var(--topbar-height) + var(--safe-area-top, 0px));
  background: var(--bg-elevated);
  border-bottom: 1px solid var(--border);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  gap: var(--space-4);

  @media (max-width: ${BP_MOBILE}px) {
    padding: var(--safe-area-top, 0px) var(--space-3) 0;
  }

  /* 375 px com todos os botoes: sem apertar, o menu da pessoa ficava fora da tela. */
  @media (max-width: 480px) {
    gap: var(--space-2);
  }
`;

export const LogoArea = styled(NavLink)`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  text-decoration: none;
  padding: 6px 10px;
  border-radius: 999px;
  transition: background 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.surfaceAlt};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 2px;
  }

  @media (max-width: 480px) {
    padding: 6px 4px;
  }
`;

// Logo completo (sigla + wordmark) - desktop e mobile-up.
export const LogoFull = styled.span`
  display: inline-flex;

  @media (max-width: 480px) {
    display: none;
  }
`;

// Logo so simbolo - mobile estreito.
export const LogoCompact = styled.span`
  display: none;

  @media (max-width: 480px) {
    display: inline-flex;
  }
`;

export const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  margin-left: auto;

  @media (max-width: 480px) {
    gap: 4px;
  }
`;

// Pill destacado para acionar a instalação como PWA. Aparece apenas
// quando o navegador emitiu `beforeinstallprompt` (capturado por
// useInstallPrompt). Usa accent para reforçar a chamada-à-ação.
export const InstallPill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 11px;
  min-height: 30px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.accent};
  background: ${({ theme }) => theme.accent};
  color: #ffffff;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: filter 0.18s ease, transform 0.18s ease;

  &:hover:not(:disabled) {
    filter: brightness(0.94);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 860px) {
    display: none;
  }
`;

// Pill "Atualizado em ..." discreto.
export const UpdatePill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 11px;
  min-height: 30px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.textSecondary};
  font-size: 11px;
  cursor: pointer;
  white-space: nowrap;
  transition: border-color 0.18s ease, color 0.18s ease;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.accent};
    color: ${({ theme }) => theme.accent};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 860px) {
    display: none;
  }
`;

export const PriceButton = styled.button`
  width: 40px;
  min-height: 40px;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 999px;
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textSecondary};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 9px;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.accent};
    border-color: ${({ theme }) => theme.accent};
  }

  &:disabled {
    opacity: 0.62;
    cursor: not-allowed;
  }
`;

// UI.1 (Bloco UI, auditoria 2026-05-23): IconButton generico do header
// (usado pelo Help icon e futuras acoes). Mesmo tamanho do PriceButton
// para visual consistente.
export const HeaderIconButton = styled.button`
  width: 36px;
  min-height: 36px;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 999px;
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textSecondary};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.accent};
    border-color: ${({ theme }) => theme.accent};
  }

  &:disabled {
    opacity: 0.62;
    cursor: not-allowed;
  }
`;

// No celular estreito o "?" sai do topo para caber o menu da pessoa, que
// tem o mesmo "Rever o tour".
export const BotaoDeAjuda = styled(HeaderIconButton)`
  @media (max-width: 480px) {
    display: none;
  }
`;

// UI.1 (Bloco UI, auditoria 2026-05-23): pill segmentado para Sun/Moon
// toggle. Dois IconButton lado a lado, com o ativo highlighted em accent.
// Insipirado nas referencias de design (segmented control claro/escuro).
export const ThemeToggleGroup = styled.div`
  display: inline-flex;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 999px;
  padding: 2px;
  background: ${({ theme }) => theme.surface};
  gap: 2px;
`;

export const ThemeToggleSegment = styled.button`
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 999px;
  background: ${({ theme, $active }) =>
    $active ? theme.surfaceAlt : "transparent"};
  color: ${({ theme, $active }) =>
    $active ? theme.accent : theme.textTertiary};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    color: ${({ theme }) => theme.accent};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 1px;
  }
`;

export const BurgerButton = styled.button`
  background: none;
  border: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme }) => theme.textSecondary};
  border-radius: 999px;
  padding: 7px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.accent};
    border-color: ${({ theme }) => theme.accent};
  }

  @media (min-width: ${MOBILE_BREAKPOINT + 1}px) {
    display: none;
  }
`;

export const UserPhoto = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 999px;
  object-fit: cover;
  display: block;
`;

// UI.3 (Bloco UI, auditoria 2026-05-23): dot indicator no avatar quando
// ha notificacoes agregadas (NotificationBell.total > 0). Posicionado no
// canto sup-direito do avatar via wrapper.
export const AvatarWrapper = styled.span`
  position: relative;
  display: inline-flex;
`;

export const StatusDot = styled.span`
  position: absolute;
  top: -2px;
  right: -2px;
  width: 9px;
  height: 9px;
  border-radius: 999px;
  background: ${({ theme }) => theme.accent};
  border: 2px solid ${({ theme }) => theme.surface};
`;

export const UserInitials = styled.span`
  width: 24px;
  height: 24px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.textPrimary};
  border: 1px solid ${({ theme }) => theme.border};
  font-size: 10px;
  font-weight: 900;
`;

// MT-4.7 (multi-tenant): pill discreta com a unidade (tenant) corrente do
// usuário logado — "UN1 — Marcenaria Exemplo". Neutra (sem cor de estado): é contexto,
// não status. Some em telas estreitas junto com o nome do usuário.
export const TenantPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 5px 11px;
  min-height: 30px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.textSecondary};
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 760px) {
    display: none;
  }
`;

export const UserMenuWrapper = styled.div`
  position: relative;
`;

export const UserMenuButton = styled.button`
  min-height: 34px;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 999px;
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textSecondary};
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 5px 9px 5px 5px;
  transition: all 0.2s ease;
  max-width: 230px;

  &:hover {
    color: ${({ theme }) => theme.accent};
    border-color: ${({ theme }) => theme.accent};
  }
`;

export const UserName = styled.span`
  max-width: 145px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 760px) {
    display: none;
  }
`;

export const UserMenu = styled(motion.div)`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 190px;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 10px;
  background: ${({ theme }) => theme.surface};
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.14);
  padding: 6px;
  z-index: 250;
`;

export const UserMenuItem = styled.button`
  width: 100%;
  min-height: 36px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 8px 10px;
  text-align: left;

  &:hover {
    color: ${({ theme }) => theme.textPrimary};
    background: ${({ theme }) => theme.surfaceAlt};
  }
`;
